// ============================================================================
// CHECK-IN PROJECTED SHORTAGE
// ============================================================================
//
// Mirrors CheckInService.ProjectCheckInShortageAsync so the desk sees the same
// answer while it is still choosing a category as it would get from the server on
// save. The server stays the enforcement point — this is the early warning, and it
// must agree with the late one or the desk learns to distrust both.
//
// Reference: crs-doc/CRS-Inventory-Shortage-and-Oversell-Design.md §7.2

import type { CheckInInventoryImpact } from 'api/types/checkin.types';

export type ShortfallKind =
  /** The category assigned at the desk is short on other nights of the stay. */
  | 'creates'
  /** The stay spans nights the category is already short on, whoever caused it. */
  | 'contributes';

export interface CategoryShortfall {
  roomCategoryId: string;
  categoryName: string;
  kind: ShortfallKind;
  /** Every affected night, ISO date strings, ascending — tonight included. */
  nights: string[];
  /**
   * The affected nights OTHER than the arrival night. Split at compute time
   * rather than re-derived in the message: the two get different sentences, and
   * working it out from `nights` alone needs the arrival date, which the
   * message formatter has no reason to know.
   */
  laterNights: string[];
  /** Worst rooms short across `laterNights`. 0 when only tonight is short. */
  rooms: number;
  /**
   * The arrival night itself is short. Called out separately because it is a
   * different decision: the assigned room is physically free, but the category
   * owes it to a booking due in later today.
   */
  includesTonight: boolean;
  /** Rooms short on the arrival night specifically. 0 when tonight is fine. */
  roomsTonight: number;
}

const dayKey = (iso: string): string => iso.slice(0, 10);

/**
 * Renders night-dates as compact ranges — "16–18 Aug 2026". Mirrors the server's
 * DateRangeFormatter so a warning reads identically wherever it is produced.
 */
export function formatNightRanges(nights: string[]): string {
  const days = Array.from(new Set(nights.map(dayKey))).sort();
  if (days.length === 0) return '';

  const asDate = (d: string) => new Date(`${d}T00:00:00`);
  const isNextDay = (a: string, b: string) => {
    const next = asDate(a);
    next.setDate(next.getDate() + 1);
    return next.toISOString().slice(0, 10) === b;
  };

  const runs: Array<[string, string]> = [];
  let start = days[0];
  let end = days[0];
  for (let i = 1; i < days.length; i += 1) {
    if (isNextDay(end, days[i])) { end = days[i]; continue; }
    runs.push([start, end]);
    start = days[i];
    end = days[i];
  }
  runs.push([start, end]);

  const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  const full = (d: string) => {
    const dt = asDate(d);
    return `${dt.getDate()} ${MONTHS[dt.getMonth()]} ${dt.getFullYear()}`;
  };

  const parts = runs.map(([from, to]) => {
    if (from === to) return full(from);
    const a = asDate(from);
    const b = asDate(to);
    if (a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth()) {
      return `${a.getDate()}–${full(to)}`;
    }
    if (a.getFullYear() === b.getFullYear()) {
      return `${a.getDate()} ${MONTHS[a.getMonth()]} – ${full(to)}`;
    }
    return `${full(from)} – ${full(to)}`;
  });

  if (parts.length === 1) return parts[0];
  if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]}`;
}

/**
 * Nights of the stay this assignment would leave short, per category.
 *
 * `roomsByCategory` is the TOTAL rooms the booking will hold in each category once
 * the desk's assignment is applied — not the change. The impact endpoint returns
 * availability with this booking's own rooms added back, so comparing a delta would
 * double-count the rooms being moved.
 *
 * The arrival night is INCLUDED. It used to be excluded because the server
 * hard-blocked on it, which turned an ordinary sold-out category into an
 * unexplainable dead end at save time. It is now confirmed like any other night —
 * it just gets its own sentence.
 */
export function computeShortfalls(
  impact: CheckInInventoryImpact | null,
  roomsByCategory: Record<string, number>,
): CategoryShortfall[] {
  if (!impact) return [];

  const arrival = dayKey(impact.arrivalNight);
  const out: CategoryShortfall[] = [];

  for (const category of impact.categories) {
    const rooms = roomsByCategory[category.roomCategoryId] ?? 0;
    if (rooms <= 0) continue;

    const nights = category.nights;

    const creates = nights.filter((n) => n.availableRooms < rooms);
    if (creates.length > 0) {
      const tonight = creates.find((n) => dayKey(n.night) === arrival);
      const later = creates.filter((n) => dayKey(n.night) !== arrival);
      out.push({
        roomCategoryId: category.roomCategoryId,
        categoryName: category.categoryName,
        kind: 'creates',
        nights: creates.map((n) => n.night),
        laterNights: later.map((n) => n.night),
        rooms: later.length > 0 ? Math.max(...later.map((n) => rooms - n.availableRooms)) : 0,
        includesTonight: !!tonight,
        roomsTonight: tonight ? rooms - tonight.availableRooms : 0,
      });
      // A category already reported as "creates" is not also reported as merely
      // spanned — one row per category, matching the server's message.
      continue;
    }

    const contributes = nights.filter((n) => n.oversoldRooms > 0);
    if (contributes.length > 0) {
      const tonight = contributes.find((n) => dayKey(n.night) === arrival);
      const later = contributes.filter((n) => dayKey(n.night) !== arrival);
      out.push({
        roomCategoryId: category.roomCategoryId,
        categoryName: category.categoryName,
        kind: 'contributes',
        nights: contributes.map((n) => n.night),
        laterNights: later.map((n) => n.night),
        rooms: later.length > 0 ? Math.max(...later.map((n) => n.oversoldRooms)) : 0,
        includesTonight: !!tonight,
        roomsTonight: tonight ? tonight.oversoldRooms : 0,
      });
    }
  }

  return out;
}

/** The desk-facing sentence for one shortfall. Matches the server's wording. */
export function shortfallMessage(s: CategoryShortfall): string {
  const parts: string[] = [];

  if (s.includesTonight) {
    // Tonight gets its own sentence: the room is physically free and assigned,
    // but the category owes it to someone due in later today. "Short 1 room on
    // 17 Aug" does not say that, and that is the decision being made.
    parts.push(
      `Assigning ${s.categoryName} takes ${s.roomsTonight} room${s.roomsTonight === 1 ? '' : 's'} `
      + 'tonight that are already committed to a booking due to arrive today — the room is '
      + 'physically free, but someone else is expected in it.',
    );
  }

  if (s.laterNights.length > 0) {
    const when = formatNightRanges(s.laterNights);
    parts.push(
      s.kind === 'creates'
        ? `Assigning ${s.categoryName} makes it short ${s.rooms} room${s.rooms === 1 ? '' : 's'} on ${when}.`
        : `This stay spans ${when}, where ${s.categoryName} is already short ${s.rooms} room${s.rooms === 1 ? '' : 's'}.`,
    );
  }

  return parts.join(' ');
}

/**
 * Nights a category would be short if the booking held `rooms` there — the number
 * behind the chip in the category dropdown. Includes the arrival night.
 */
export function shortNightsForCategory(
  impact: CheckInInventoryImpact | null,
  roomCategoryId: string,
  rooms: number,
): number {
  if (!impact || rooms <= 0) return 0;
  const category = impact.categories.find((c) => c.roomCategoryId === roomCategoryId);
  if (!category) return 0;
  // Counts the arrival night too — it is a night the desk has to decide about,
  // and omitting it under-reported the chip on exactly the category that was
  // about to be refused at save.
  return category.nights.filter(
    (n) => n.availableRooms < rooms || n.oversoldRooms > 0,
  ).length;
}
